import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post } from "~/Services";
import { eramanApiURL } from "~/Utils";
import { fields } from "../constants";
import { fetchEpcIdData } from "./FetchEpcId";
import { useMemberDocumentDetails } from "./Members.hook";

export const AddEditMembers = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const [epcOptions, setEpcOptions] = useState<any[]>([]);

  const excludedFields = [
    "dateOfBirth",
    "passportNumber",
    "nric",
    ...(query.get("id") || query.get("editId") ? [] : ["suspended"]),
  ];

  useEffect(() => {
    const loadInitialEpcOptions = async () => {
      try {
        const data = await fetchEpcIdData(""); // empty search = default API response
        setEpcOptions(data);
      } catch (error) {
        console.error("Error loading initial EPC list:", error);
      }
    };

    loadInitialEpcOptions();
  }, []);

  const modifiedFields = fields.registeredUsers
    .filter((field: any) => !excludedFields.includes(field.name))
    .map((field: any) => {
      if (field.name === "epcId") {
        return {
          ...field,
          component: "Select",
          showSearch: true,
          filterOption: false,
          options: epcOptions, // <-- options now controlled by React state
          onSearch: async (value: string) => {
            if (!value) {
              setEpcOptions([]); // clear dropdown if input is empty
              return;
            }
            try {
              const data = await fetchEpcIdData(value); // call API with search
              setEpcOptions(data); // update state -> re-renders <Select>
            } catch (error) {
              console.error("Error fetching EPC list:", error);
            }
          },
          onChange: async (value: any, form: any) => {
            try {
              const response = await axios.get(`${eramanApiURL}/admin/epc/get?id=${value?.data?.id}`);
              const selectedEpc = response.data;

              ["userName", "firstName", "emailAddress", "dateOfBirth", "nric", "passportNumber"].forEach(
                (fieldName) => {
                  form.setValue(fieldName, selectedEpc[fieldName] || "");
                }
              );
            } catch (error) {
              console.error("Error fetching EPC data:", error);
            }
          },
          editable: !(query.get("id") || query.get("editId")),
        };
      }
      return { ...field, editable: field.name === "suspended" };
    });

  const {
    data: editMembers,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchEditMember,
  } = useMemberDocumentDetails(query.get("editId") || "");

  const {
    data: membersById,
    isLoading: isLoadingMembers,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEditMember();
    }
  }, [query.get("editId"), query.get("id")]);

  const membersFormConfig: FormConfig = {
    initialValues: {
      // userName: membersById?.payload ? decodeHTML(membersById?.payload?.userName) : decodeHTML(editMembers?.userName),
      // dateOfBirth: membersById?.payload
      //   ? decodeHTML(membersById?.payload?.dateOfBirth)
      //   : decodeHTML(editMembers?.dateOfBirth),
      // nric: membersById?.payload ? decodeHTML(membersById?.payload?.nric) : decodeHTML(editMembers?.nric),
      // passportNumber: membersById?.payload
      //   ? decodeHTML(membersById?.payload?.passportNumber)
      //   : decodeHTML(editMembers?.passportNumber),
      // suspended: membersById?.payload
      //   ? decodeHTML(membersById?.payload?.suspended)
      //   : decodeHTML(editMembers?.suspended),

      firstName: membersById?.payload
        ? decodeHTML(membersById?.payload?.firstName)
        : decodeHTML(editMembers?.firstName),
      emailAddress: membersById?.payload
        ? decodeHTML(membersById?.payload?.emailAddress)
        : decodeHTML(editMembers?.emailAddress),
      epcId: membersById?.payload ? decodeHTML(membersById?.payload?.epcId) : decodeHTML(editMembers?.epcId),
    },
    fields: modifiedFields,
  };

  const onSubmit = async (data: FieldValues, subscriptionUrl: string) => {
    let imageKeys: any = [
      "fbImage",
      "field_purchase_login_pop_image",
      "field_login_image",
      "filledImage",
      "googleImage",
      "showImage",
      "field_logout_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      emailAddress: data.emailAddress,
      published: true,
    };

    try {
      let response;
      await axios.post(subscriptionUrl, payload);
      openToast({
        content: "Data Saved Successfully",
        type: "success",
      });
      navigate("/dedicated/eraman/newsletterSubscription?activeTab=registeredUsers");
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data || "An unexpected error occurred.";
        message.error(errorMessage);
      } else {
        message.error("Network error or server not reachable.");
      }
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingMembers}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Member`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingMembers || drilldownLoading}
        config={membersFormConfig}
        hideButton
        finalData={(data: FieldValues) => {
          onSubmit(data, `${eramanApiURL}/common/subscribe`);
        }}
      />
    </ContentWrapper>
  );
};
